<template>
  <v-row
    ref="cardWithTitle"
    no-gutters
    class="card-with-title-container"
  >
    <div class="card-with-title__header">
      <DataTypeBadge
        v-model="card.dataType"
      />

      <div
        :id="card.id"
        v-tooltip="showTitleTooltip && shouldDisableChanges ? title : ''"
        class="card-with-title__wrapper"
      >
        <TitleField
          :id="card.id"
          :title="title"
          :disabled="shouldDisableChanges"
          @set-title="setTitle"
          @set-show-title-tooltip="setShowTitleTooltip"
        />

        <div
          v-if="showOriginalTitle"
          class="card-with-title__original-title"
        >
          {{ $t('components.models.CardWithTitle.label.original') }}
          {{ originalTitleDisplay }}
        </div>

        <v-row
          v-if="showSaveOptions"
          align="center"
          class="input-controller-container"
          justify="end"
          no-gutters
        >
          <InputController
            :disable-save="hasMoreCharThanLimit"
            @save="saveText"
            @cancel="cancelChanges"
          />
          <div
            class="card-with-title__character-count"
            :style="charactersCounterColor"
          >
            {{ title.length }} / {{ maxChartsInTitle }}
          </div>
        </v-row>
      </div>

      <div class="card-with-title__header-actions">
        <v-btn
          v-tooltip="reloadButtonText"
          :aria-label="reloadButtonText"
          class="ds-button ds-button--tertiary ds-button--default ds-button--icon card-with-title__reload-button"
          icon
          @click="reload({ forceRefresh: true })"
        >
          <v-icon small>
            mdi-refresh
          </v-icon>
        </v-btn>
        <slot name="top-right-slot" />
        <OptionsButton
          v-if="showOptionsButton"
          :has-not-classified="hasNotClassified"
          :options-data="optionsData"
          :expanded-mode="card.props.isExpandedCard"
          @on-option-menu-click="onOptionMenuClick"
          @on-show-not-classified="onShowNotClassified"
        />
      </div>
    </div>

    <CustomErrorMessage
      v-if="hasCustomError"
      :request-status="requestStatus"
    />
    <StReloadAndLoaderWrapper
      v-else
      :failed-to-load-text="errorMessage"
      :reload-button-text="reloadButtonText"
      :request-status="requestStatus"
      @reload="reload"
    >
      <template #wrappedComponent>
        <div class="card-with-title-wrapper">
          <div class="content">
            <slot />
          </div>
        </div>
      </template>
    </StReloadAndLoaderWrapper>
  </v-row>
</template>

<script>
import StReloadAndLoaderWrapper from '@stilingue/st-reload-and-loader-wrapper';
import requestStatusMixin from '@/mixins/request-status.mixin';
import { mapGetters, mapState } from 'vuex';
import cardsModel from '@/mixins/cards-model';
import blipModel from '@/mixins/blip-model';
import InputController from '@/components/cards/models/InputController';
import { generateRandomId } from '@/utils/math';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import DataTypeBadge from '@/components/DataTypeBadge';
import TitleField from '@/components/TitleField';
import OptionsButton from '@/components/OptionsButton';
import CustomErrorMessage from '@/components/CustomErrorMessage';
import { Flags } from '@/plugins/rollout';
import { resolveCardTitle } from '@/utils/card-title-resolver';

export default {
  name: 'CardWithTitle',
  components: {
    StReloadAndLoaderWrapper,
    InputController,
    DataTypeBadge,
    TitleField,
    OptionsButton,
    CustomErrorMessage,
  },
  mixins: [
    requestStatusMixin,
    cardsModel,
    blipModel,
  ],
  props: {
    requestStatus: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    optionsData: {
      type: Array,
      default: () => ([]),
    },
    hasNotClassified: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      originalTitle: '',
      title: '',
      maxChartsInTitle: 100,
      inputLoading: false,
      componentWidth: 0,
      showTitleTooltip: false,
    };
  },
  computed: {
    errorMessage() {
      return this.$t('components.models.CardWithTitle.label.errorMessage');
    },
    reloadButtonText() {
      return this.$t('components.models.CardWithTitle.label.reloadButtonText');
    },
    ...mapGetters('filter', [
      'formattedDateRange',
    ]),
    ...mapState('misc', [
      'forceRefresh',
      'silentRefresh',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    hasCustomError() {
      return Boolean(!['success', 'error', 'loading'].includes(this.requestStatus));
    },
    showSaveOptions() {
      return this.originalTitle !== this.title;
    },
    hasMoreCharThanLimit() {
      return this.title.length > this.maxChartsInTitle || Boolean(!this.title.length);
    },
    isExpandedCard() {
      return this.card.props.isExpandedCard;
    },
    showOriginalTitle() {
      return this.isExpandedCard;
    },
    originalTitleDisplay() {
      return this.card.props.originalTitle || this.$t('components.models.CardWithTitle.label.originalNotFound');
    },
    showNewCardMenuFeature() {
      return this.isEnabled(Flags.showNewCardMenu);
    },
    showOptionsButton() {
      return Boolean(this.optionsData.length);
    },
    charactersCounterColor() {
      return this.hasMoreCharThanLimit ? 'color: var(--ds-color-text-error)' : 'color: var(--ds-color-text-2);';
    },
  },
  watch: {
    '$i18n.locale'() {
      if (!this.card.props.titleCustomized) {
        const resolved = resolveCardTitle(this.card, this);
        this.originalTitle = resolved;
        this.title = resolved;
      }
    },
    formattedDateRange() {
      this.reload({
        forceRefresh: true,
      });
    },
    forceRefresh() {
      if (this.forceRefresh) {
        this.reload({
          forceRefresh: true,
        });
      }
    },
    silentRefresh() {
      if (this.silentRefresh) {
        this.reload({
          silentRefresh: true,
        });
      }
    },
  },
  created() {
    const resolvedTitle = resolveCardTitle(this.card, this);
    this.originalTitle = resolvedTitle;
    this.title = resolvedTitle;
  },
  methods: {
    reload({ silentRefresh, forceRefresh } = {}) {
      this.$emit('reload', { silentRefresh, forceRefresh });
    },
    saveText() {
      this.inputLoading = true;
      this.$nextTick(() => {
        this.card.props.titleCustomized = true;
        this.card.props.title = this.title.replace(/[\n]/u, '');
        if (this.card.props.isExpandedCard) this.card.props.isExpandedCard = false;
        trackEventWithUserProperties('ST Card Title Edit', { Title: this.card.props.title });
        this.card.id = generateRandomId();
        this.saveCards().then(() => {
          this.originalTitle = this.title;
          this.inputLoading = false;
        });
      });
    },
    cancelChanges() {
      this.title = this.originalTitle;
    },
    setTitle(value) {
      this.title = value;
    },
    setShowTitleTooltip(value) {
      this.showTitleTooltip = value;
    },
    onShowNotClassified() {
      this.$emit('on-show-not-classified');
    },
    onOptionMenuClick(menuMethodName) {
      this.$emit('call-option-menu', menuMethodName);
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~vuetify/src/styles/main.sass';
  $st-color-text-light: #{map-deep-get($material-light, 'text', 'primary')};
  $st-color-text-dark: #{map-deep-get($material-dark, 'text', 'primary')};

  $original-title-top-position: 64px;

  .card-with-title {
    &__header {
      display: flex;
      justify-content: stretch;
      align-items: flex-start;
      margin-bottom: 22px;
      width: 100%;
      align-self: center;
    }

    &__header-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    &__wrapper {
      width: 100%;
      display: block;
    }

    &__character-count {
      font-style: normal;
      font-weight: var(--ds-font-weight-regular);
      font-size: var(--ds-font-size-xs);
      line-height: var(--ds-font-line-height-dense);
      margin-top: 3px;
    }

    &__original-title {
      position: absolute;
      top: $original-title-top-position;
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-xs);
      color: var(--ds-color-text-2);
      line-height: 16px;
    }
  }

  .card-with-title-wrapper {
    width: 100%;
    height: 100%;
  }

  .card-with-title-container {
    display: grid;
    grid-template-rows: 75px auto;
    width: 100%;
    height: 100%;
    justify-items: center;
    align-items: center;
  }

  .content {
    width: 100%;
    height: 100%;
  }

  .reload-and-loader-wrapper {
    height: 100%;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 5px;
    }
  }

  @media screen and (max-width: 600px) {
    .title {
      max-width: 250px;
    }
  }

  .input-controller-container {
    position: absolute;
    top: 55px;
    right: 70px;
    width: 125px;
    z-index: 99;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
  }

  ::v-deep {
    .input-controller-container {
      .v-btn {
        margin-bottom: 5px;
      }
    }

    .v-input__slot {
      padding: 0 !important;
    }

    .v-text-field {
      padding-top: 0;
      margin-top: 0;
    }

    .v-text-field__slot {
      display: flex;
      flex-direction: column;
    }

    .st-vue-loader {
      z-index: 1;
    }
  }
</style>
